import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import { ClockIcon } from '@phosphor-icons/react';
import { Card, CircularProgress, Markdown, StepperProgress, TagPill, TipTapEditorWithComments, type TipTapJson } from '@repo/ui';
import type { Editor } from '@tiptap/react';
import React, { useRef } from 'react';

interface FeedbackItem {
  quotedText: string;
  comment: string;
  type: "positive" | "negative" | "neutral";
}

interface GradingUIProps {
  // Essay content
  essayContent: string;
  onEssayChange?: (text: string, tipTapJson: TipTapJson) => void;

  // Feedback and scoring
  score?: number;
  generalFeedback?: string;
  feedback?: FeedbackItem[];

  // Character/word counting
  isCharacterLimit?: boolean;
  limit?: number;
  onCharacterCountChange?: (char: number, word: number) => void;

  // Editor configuration
  readOnly?: boolean;
  placeholder?: string;

  // Actions
  onRequestFeedback?: () => void;
  onSaveDraft?: () => void;

  // Loading states
  isGeneratingFeedback?: boolean;
  isSavingDraft?: boolean;

  // Save status
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
  isDirty?: boolean;
  onSave?: () => void;

  // Essay history
  historyId?: string;
  versionKeys?: string[];
  onSelectVersion?: (versionKey: string) => void;
}

const GradingUI: React.FC<GradingUIProps> = ({
  essayContent,
  onEssayChange,
  score,
  generalFeedback,
  feedback = [],
  isCharacterLimit = false,
  limit = 4000,
  onCharacterCountChange,
  readOnly = false,
  placeholder = "Write your personal statement here.",
  onRequestFeedback,
  onSaveDraft,
  isGeneratingFeedback = false,
  isSavingDraft = false,
  saveStatus = 'idle',
  isDirty = false,
  onSave,
  historyId,
  versionKeys = [],
  onSelectVersion
}) => {
  const editorRef = useRef<Editor | null>(null);
  const [count, setCount] = React.useState(0);

  const handleCharacterCountChange = (char: number, word: number) => {
    const currentCount = isCharacterLimit ? char : word;
    setCount(currentCount);
    onCharacterCountChange?.(char, word);
  };

  const percent = Math.min(Math.round((count / limit) * 100), 100);

  const hasHistory = historyId && versionKeys.length > 0;

  const formatTimestamp = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      if (isToday) {
        return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return date.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return isoString;
    }
  };

  // Sort versions by timestamp (newest first)
  const sortedVersions = [...versionKeys].sort((a, b) =>
    new Date(b).getTime() - new Date(a).getTime()
  );

  return (
    <Stack spacing={2}>
      {/* Score and Feedback Display */}
      {score && (
        <Stack direction="row" alignItems="stretch" spacing={2}>
          {/* Left Column: Score + History */}
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Card sx={{ height: 250 }}>
              <Stack direction='column' alignItems='stretch' width='100%' justifyContent='space-between' height='100%'>
                <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600 }}>
                  How Ready Is Your Essay? 📝
                </Typography>
                <StepperProgress score={score} />
                <Typography variant="caption">
                  Remember, there's always room to make your essay even better! But when you see that green light 🟢, you're good to go! 🚀
                </Typography>
              </Stack>
            </Card>


            <Card sx={{ flex: 1, height: 300, maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Essay History
                </Typography>
                {hasHistory && <TagPill
                  color='grey'
                  text={`${versionKeys.length} version${versionKeys.length === 1 ? '' : 's'}`}
                />}
              </Stack>
              {hasHistory ? <Box sx={{ flex: 1, overflow: 'auto', mx: -2 }}>
                <List disablePadding>
                  {sortedVersions.map((versionKey, index) => (
                    <ListItemButton
                      key={versionKey}
                      onClick={() => onSelectVersion?.(versionKey)}
                      disabled={!historyId}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <ClockIcon size={20} />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatTimestamp(versionKey)}
                        secondary={index === 0 ? 'Latest version' : `Version ${sortedVersions.length - index}`}
                        slotProps={{
                          primary: {
                            variant: 'body2',
                            fontWeight: index === 0 ? 'medium' : 'normal'
                          },
                          secondary: {
                            variant: 'caption'
                          }
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
                : <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>Tip: When you request feedback, we automatically save your previous version for you to compare. We save both the essay and the feedback!</Typography>
                </Box>}
            </Card>

          </Stack>

          {/* Right Column: Overall Feedback */}
          {generalFeedback && (
            // 566 = 250 + 300 + 16 (spacing)
            <Card sx={{ flex: 2, height: 566, maxHeight: 566 }}>
              <Stack direction='column' alignItems='stretch' width='100%' justifyContent='space-between' height='100%' spacing={2}>
                <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 600 }}>
                  Overall Feedback 📋
                </Typography>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <Markdown text={generalFeedback} hideBorder />
                </Box>
              </Stack>
            </Card>
          )}
        </Stack>
      )}

      {/* Editor */}
      <TipTapEditorWithComments
        editorRef={editorRef}
        initialContent={essayContent}
        onChange={onEssayChange}
        placeholder={placeholder}
        readOnly={readOnly}
        highlightedTextList={feedback.map((item, index) => ({
          quotedText: item.quotedText,
          comment: item.comment,
          type: item.type,
          id: index.toString()
        }))}
        onCharacterCountChange={handleCharacterCountChange}
      />

      {/* Bottom Controls */}
      {!readOnly && (
        <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
          <Typography
            variant="caption"
            color="text.secondary"
            onClick={isDirty && onSave ? onSave : undefined}
            sx={{
              cursor: isDirty && onSave ? 'pointer' : 'default',
              fontWeight: isDirty ? 'medium' : 'normal'
            }}
          >
            {saveStatus === 'saving' ? 'Saving...' : isDirty ? 'Unsaved changes' : 'Saved'}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress
              label={isCharacterLimit ? `${count} / ${limit} characters` : `${count} / ${limit} words`}
              value={percent}
              variant='determinate'
              error={count > limit}
            />

            {onSaveDraft && (
              <Button
                variant="outlined"
                disabled={!essayContent || isSavingDraft}
                onClick={onSaveDraft}
                size="small"
                loading={isSavingDraft}
                loadingPosition='start'
              >
                {isSavingDraft ? 'Saving...' : 'Save Draft'}
              </Button>
            )}

            {onRequestFeedback && (
              <Button
                variant="contained"
                color="primary"
                disabled={!essayContent || isGeneratingFeedback}
                onClick={onRequestFeedback}
                loading={isGeneratingFeedback}
                loadingPosition='start'
              >
                {isGeneratingFeedback ? 'Analyzing...' : 'Request Feedback'}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default GradingUI; 